// Host overrides for the invitation's look: one accent colour, one background
// colour and one font pairing, each optional.
//
// The invitation's CSS (InvitationPage.css) drives every colour and face
// through custom properties on `.inv`. This module turns the three stored
// fields into an inline style object of those properties, so an override
// repaints the whole design — title, buttons, rules, paper — without touching
// a single template rule.
//
// Empty field = no override at all: the template's own CSS applies untouched.
// That is deliberate — the templates are designed looks, and their palette and
// type are what the Design step offers as the default (see `defaults` on each
// InviteTemplate). The picker shows those defaults as pre-selected; only a
// value the host actually changes is stored and overrides the design.

import type { CSSProperties } from 'react'

/* ---------------------------------------------------------------- colours */

const clamp255 = (n: number) => Math.max(0, Math.min(255, Math.round(n)))

/** #rgb / #rrggbb → [r, g, b]. null for anything unparseable. */
const hexToRgb = (hex?: string | null): [number, number, number] | null => {
  const v = (hex || '').trim().replace(/^#/, '')
  const full = v.length === 3 ? v.replace(/./g, (c) => c + c) : v
  if (!/^[0-9a-f]{6}$/i.test(full)) return null
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ]
}

const rgbToHex = ([r, g, b]: [number, number, number]) =>
  `#${[r, g, b].map((n) => clamp255(n).toString(16).padStart(2, '0')).join('')}`

/** Blend `hex` toward `target` by `amount` (0 = unchanged, 1 = target). */
const mix = (hex: string, target: [number, number, number], amount: number) => {
  const rgb = hexToRgb(hex)
  if (!rgb) return hex
  return rgbToHex([
    rgb[0] + (target[0] - rgb[0]) * amount,
    rgb[1] + (target[1] - rgb[1]) * amount,
    rgb[2] + (target[2] - rgb[2]) * amount,
  ])
}

/** Darker version of a colour. */
export const shade = (hex: string, amount: number) => mix(hex, [0, 0, 0], amount)
/** Lighter version of a colour. */
export const tint = (hex: string, amount: number) => mix(hex, [255, 255, 255], amount)
/** Blend `hex` toward another hex colour by `amount` (0 = unchanged, 1 = target). */
export const blend = (hex: string, target: string, amount: number) => {
  const rgb = hexToRgb(target)
  return rgb ? mix(hex, rgb, amount) : hex
}

/** #rgb / #rrggbb, with or without the hash → `#rrggbb`. '' for anything else. */
export const cleanHex = (value?: string | null) => {
  const rgb = hexToRgb(value)
  return rgb ? rgbToHex(rgb) : ''
}

export type Hsv = { h: number; s: number; v: number }

const clamp01 = (n: number) => Math.max(0, Math.min(1, n))

export const hexToHsv = (hex: string): Hsv => {
  const [r, g, b] = (hexToRgb(hex) || [0, 0, 0]).map((n) => n / 255)
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  let h = 0
  if (d) {
    if (max === r) h = ((g - b) / d) % 6
    else if (max === g) h = (b - r) / d + 2
    else h = (r - g) / d + 4
  }
  return { h: (h * 60 + 360) % 360, s: max ? d / max : 0, v: max }
}

export const hsvToHex = ({ h, s, v }: Hsv) => {
  const sector = (((h % 360) + 360) % 360) / 60
  const c = clamp01(v) * clamp01(s)
  const x = c * (1 - Math.abs((sector % 2) - 1))
  const m = clamp01(v) - c
  const [r, g, b] =
    sector < 1 ? [c, x, 0]
      : sector < 2 ? [x, c, 0]
        : sector < 3 ? [0, c, x]
          : sector < 4 ? [0, x, c]
            : sector < 5 ? [x, 0, c]
              : [c, 0, x]
  return rgbToHex([(r + m) * 255, (g + m) * 255, (b + m) * 255])
}

/**
 * A colour a host typed or pasted, in whichever notation they had it in:
 * `#a1b2c3`, `a1b2c3`, `rgb(161, 178, 195)`, `rgb(63% 70% 76%)`,
 * `161,178,195`, `hsl(210 22% 76%)` or `hsv(210, 17%, 76%)` (`hsb` too).
 * A component written with a `%` is read as a percentage; without one, a
 * number over 1 is still read as a percentage, so `hsl(210 22 76)` works.
 * That last guess is why the `%` has to be looked at and not just the
 * magnitude: `hsl(220, 13%, 1%)` is very nearly black, and reading its 1 as
 * a ratio would hand back white.
 * Returns `#rrggbb`, or '' when there is no colour in the text.
 */
export const parseColour = (input?: string | null) => {
  const text = String(input || '').trim().toLowerCase()
  if (!text) return ''
  const asHex = cleanHex(text.startsWith('#') ? text : `#${text}`)
  if (asHex) return asHex
  const fn = /^(rgba?|hsla?|hsva?|hsba?)\s*\(([^)]*)\)$/.exec(text)
  const body = fn ? fn[2] : text
  const parts = body.split(/[\s,/]+/).filter(Boolean).slice(0, 3)
  if (parts.length < 3) return ''
  const n = parts.map((part) => parseFloat(part))
  if (n.some((x) => Number.isNaN(x))) return ''
  const written = parts.map((part) => part.includes('%'))
  const ratio = (i: number) => clamp01(written[i] || n[i] > 1 ? n[i] / 100 : n[i])
  const model = fn ? (fn[1][0] === 'r' ? 'rgb' : fn[1].slice(0, 3)) : 'rgb'
  if (model === 'rgb') {
    const channel = (i: number) => (written[i] ? clamp01(n[i] / 100) * 255 : n[i])
    return rgbToHex([channel(0), channel(1), channel(2)])
  }
  if (model === 'hsl') {
    const l = ratio(2)
    const v = l + ratio(1) * Math.min(l, 1 - l)
    return hsvToHex({ h: n[0], s: v ? 2 * (1 - l / v) : 0, v })
  }
  return hsvToHex({ h: n[0], s: ratio(1), v: ratio(2) })
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export const luminance = (hex: string): number => {
  const rgb = hexToRgb(hex)
  if (!rgb) return 1
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * Is this dark enough that text on it has to be light? The threshold sits
 * where a mid-tone stops carrying dark type comfortably, not at 50% grey.
 */
export const isDarkColor = (hex: string) => luminance(hex) < 0.35

/** WCAG contrast ratio between two colours, 1 (identical) to 21. */
const contrastRatio = (a: string, b: string) => {
  const la = luminance(a)
  const lb = luminance(b)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/**
 * Move `color` away from `paper` until it is readable on it.
 *
 * A host can pair any accent with any background, and the honest pairings are
 * the ones that go wrong: navy type on a charcoal paper is a colour they chose
 * deliberately and cannot read. Rather than refusing the combination, keep
 * their hue and walk its lightness until it clears the target ratio.
 * 3.2:1 is the large-text bar — the accent is used at title and caps sizes.
 */
const legibleOn = (color: string, paper: string, target = 3.2) => {
  if (contrastRatio(color, paper) >= target) return color
  const lighten = luminance(paper) < luminance(color) || isDarkColor(paper)
  let out = color
  for (let step = 1; step <= 12; step += 1) {
    out = lighten ? tint(color, step * 0.08) : shade(color, step * 0.08)
    if (contrastRatio(out, paper) >= target) break
  }
  return out
}

/* ------------------------------------------------------------------ fonts */

export type InviteFont = {
  id: string
  label: { en: string; ka: string }
  /** The event name. */
  display: string
  /** Body copy — paragraphs, schedule rows, descriptions. */
  body: string
  /** Controls: buttons, fields, choices. Falls back to `body`. */
  ui?: string
  /** Google Fonts stylesheet this pairing needs. */
  href?: string
  /** Sample rendered on the picker chip. */
  sample?: string
}

const KA_SERIF = `'Noto Serif Georgian', Georgia, 'Sylfaen', serif`
const KA_SANS = `'Noto Sans Georgian', system-ui, sans-serif`

/**
 * The Georgian tail of a stack: a companion face, then the Noto backstop.
 *
 * Google Fonts ships two Georgian families and no more, so until these faces
 * existed every display face below handed its ქართული straight to a plain
 * Noto — a copperplate wedding title and an 8-bit party title came out in the
 * same sans, beside English that did not. Each `ka(...)` names the Georgian
 * face chosen to match the Latin one in front of it; `index.css` declares them
 * and `assets/fonts/README.md` records which backs which.
 *
 * Noto stays last on purpose: it is the only Georgian face here that covers
 * every block, so an archaic or ecclesiastical character still renders.
 */
const ka = (face: string, base = KA_SANS) => `'${face}', ${base}`

// One entry per look the templates use, plus a few neutral pairings. Every
// template's `defaults.font` points at one of these ids.
export const INVITE_FONTS: InviteFont[] = [
  {
    id: 'cormorant',
    label: { en: 'Cormorant — classic serif', ka: 'Cormorant — კლასიკური' },
    display: `'Cormorant Garamond', ${ka('Geo Times', KA_SERIF)}`,
    body: `'Cormorant Garamond', ${ka('Geo Times', KA_SERIF)}`,
    href: 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap',
  },
  {
    id: 'pinyon-bricolage',
    label: { en: 'Pinyon Script — calligraphy', ka: 'Pinyon Script — კალიგრაფია' },
    display: `'Pinyon Script', ${ka('MCh Vardi Serif', KA_SERIF)}`,
    body: `'Bricolage Grotesque', ${ka('Mersad', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@400;500;600;700&family=Pinyon+Script&display=swap',
  },
  {
    id: 'great-vibes',
    label: { en: 'Great Vibes — romantic', ka: 'Great Vibes — რომანტიული' },
    display: `'Great Vibes', ${ka('MCh Vardi Serif', KA_SERIF)}`,
    body: `'Cormorant Garamond', ${ka('Geo Times', KA_SERIF)}`,
    href: 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400&family=Great+Vibes&display=swap',
  },
  {
    id: 'playfair-montserrat',
    label: { en: 'Playfair — editorial', ka: 'Playfair — ჟურნალური' },
    display: `'Playfair Display', ${ka('BPG Serif', KA_SERIF)}`,
    body: `'Montserrat', ${ka('Mersad', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@500;600;700&family=Playfair+Display:ital,wght@0,700;1,400&display=swap',
  },
  {
    id: 'baloo-delius',
    label: { en: 'Baloo — round & playful', ka: 'Baloo — მხიარული' },
    display: `'Baloo 2', ${ka('ALK Rounded', KA_SERIF)}`,
    body: `'Delius', ${ka('MG Niniko', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Baloo+2:wght@600;800&family=Delius&display=swap',
  },
  {
    id: 'glass-pompiere',
    label: { en: 'Glass Antiqua — retro', ka: 'Glass Antiqua — რეტრო' },
    display: `'Glass Antiqua', ${ka('BPG Nostalgia', KA_SERIF)}`,
    body: `'Pompiere', ${ka('BPG Nostalgia', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Glass+Antiqua&family=Pompiere&display=swap',
  },
  {
    id: 'archivo',
    label: { en: 'Archivo — modern', ka: 'Archivo — თანამედროვე' },
    display: `'Archivo', ${ka('Mersad', KA_SANS)}`,
    body: `'Archivo', ${ka('Mersad', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Archivo:wdth,wght@62..125,100..900&display=swap',
  },
  {
    id: 'dmserif-inter',
    label: { en: 'DM Serif — clean', ka: 'DM Serif — მკაფიო' },
    display: `'DM Serif Display', ${ka('BPG Serif', KA_SERIF)}`,
    body: `'Inter', ${ka('Mersad', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Inter:wght@400;500;600&display=swap',
  },
  // ---- pairings added with the template library (2026-09-03) ----
  // Every display face below is Latin-only, so each carries a `ka(...)`
  // companion in the same style — the same arrangement as the original eight.
  {
    id: 'fredoka',
    label: { en: 'Fredoka — round & friendly', ka: 'Fredoka — მრგვალი და მეგობრული' },
    display: `'Fredoka', ${ka('ALK Rounded', KA_SANS)}`,
    body: `'Nunito', ${ka('ALK Rounded', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Fredoka:wght@500;600;700&family=Nunito:wght@400;600;700&display=swap',
  },
  {
    id: 'bangers',
    label: { en: 'Bangers — comic & loud', ka: 'Bangers — კომიქსური' },
    display: `'Bangers', ${ka('SS Geo Hakuna', KA_SANS)}`,
    body: `'Nunito', ${ka('ALK Rounded', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Bangers&family=Nunito:wght@400;600;700&display=swap',
  },
  {
    id: 'pacifico',
    label: { en: 'Pacifico — playful script', ka: 'Pacifico — მხიარული ხელნაწერი' },
    display: `'Pacifico', ${ka('Geo Brush Script', KA_SERIF)}`,
    body: `'Quicksand', ${ka('ALK Rounded', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Pacifico&family=Quicksand:wght@400;500;600;700&display=swap',
  },
  {
    id: 'parisienne',
    label: { en: 'Parisienne — fairytale', ka: 'Parisienne — ზღაპრული' },
    display: `'Parisienne', 'Geo Script', ${ka('MCh Vardi', KA_SERIF)}`,
    body: `'Cormorant Garamond', ${ka('Geo Times', KA_SERIF)}`,
    href: 'https://fonts.googleapis.com/css2?family=Parisienne&family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400&display=swap',
  },
  {
    id: 'caveat',
    label: { en: 'Caveat — handwritten', ka: 'Caveat — ხელნაწერი' },
    display: `'Caveat', ${ka('MG Niniko', KA_SANS)}`,
    body: `'Nunito', ${ka('ALK Rounded', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Caveat:wght@500;600;700&family=Nunito:wght@400;600;700&display=swap',
  },
  {
    id: 'luckiest-guy',
    label: { en: 'Luckiest Guy — cartoon', ka: 'Luckiest Guy — მულტფილმური' },
    display: `'Luckiest Guy', ${ka('Geo Picasso', KA_SANS)}`,
    body: `'Nunito', ${ka('ALK Rounded', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Luckiest+Guy&family=Nunito:wght@400;600;700&display=swap',
  },
  {
    id: 'chewy',
    label: { en: 'Chewy — bubbly', ka: 'Chewy — ბუშტუკებიანი' },
    display: `'Chewy', ${ka('TF Mecomicse', KA_SANS)}`,
    body: `'Nunito', ${ka('ALK Rounded', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Chewy&family=Nunito:wght@400;600;700&display=swap',
  },
  {
    id: 'quicksand',
    label: { en: 'Quicksand — soft & light', ka: 'Quicksand — რბილი და მსუბუქი' },
    display: `'Quicksand', ${ka('ALK Rounded', KA_SANS)}`,
    body: `'Quicksand', ${ka('ALK Rounded', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&display=swap',
  },
  {
    id: 'orbitron',
    label: { en: 'Orbitron — sci-fi', ka: 'Orbitron — ფანტასტიკური' },
    display: `'Orbitron', ${ka('Eurostile Geo', KA_SANS)}`,
    body: `'Space Grotesk', ${ka('Eurostile Geo', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Orbitron:wght@500;700;900&family=Space+Grotesk:wght@400;500;600&display=swap',
  },
  {
    id: 'press-start',
    label: { en: 'Press Start — 8-bit', ka: 'Press Start — 8-ბიტიანი' },
    display: `'Press Start 2P', ${ka('MG Press Start', KA_SANS)}`,
    body: `'Space Grotesk', ${ka('Eurostile Geo', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Press+Start+2P&family=Space+Grotesk:wght@400;500;600&display=swap',
  },
  {
    id: 'abril',
    label: { en: 'Abril — bold editorial', ka: 'Abril — ჟურნალური' },
    display: `'Abril Fatface', ${ka('Geo Picasso', KA_SERIF)}`,
    body: `'Lora', ${ka('BPG Serif', KA_SERIF)}`,
    href: 'https://fonts.googleapis.com/css2?family=Abril+Fatface&family=Lora:ital,wght@0,400;0,500;1,400&display=swap',
  },
  {
    id: 'cinzel',
    label: { en: 'Cinzel — classic capitals', ka: 'Cinzel — კლასიკური' },
    display: `'Cinzel', ${ka('GL Kupiura', KA_SERIF)}`,
    body: `'Cormorant Garamond', ${ka('Geo Times', KA_SERIF)}`,
    href: 'https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Cormorant+Garamond:ital,wght@0,400;0,500;1,400&display=swap',
  },
  {
    id: 'bebas',
    label: { en: 'Bebas — bold poster', ka: 'Bebas — პლაკატური' },
    display: `'Bebas Neue', ${ka('LGV Bebas Neue', KA_SANS)}`,
    body: `'Inter', ${ka('Mersad', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600&display=swap',
  },
  {
    id: 'lobster',
    label: { en: 'Lobster — retro script', ka: 'Lobster — რეტრო' },
    display: `'Lobster', ${ka('SS Geo Hakuna', KA_SERIF)}`,
    body: `'Lato', ${ka('Mersad', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Lobster&family=Lato:wght@400;700&display=swap',
  },
  {
    id: 'delafield-cormorant',
    label: { en: 'Mrs Saint Delafield — signature', ka: 'Mrs Saint Delafield — ხელმოწერა' },
    display: `'Mrs Saint Delafield', ${ka('MCh Vardi Serif', KA_SERIF)}`,
    body: `'Cormorant Garamond', ${ka('Geo Times', KA_SERIF)}`,
    href: 'https://fonts.googleapis.com/css2?family=Mrs+Saint+Delafield&family=Cormorant+Garamond:wght@500;600;700&display=swap',
  },
  {
    id: 'pinyon-montserrat',
    label: { en: 'Pinyon Script — with Montserrat', ka: 'Pinyon Script — Montserrat-თან' },
    display: `'Pinyon Script', ${ka('MCh Vardi Serif', KA_SERIF)}`,
    body: `'Montserrat', ${ka('Mersad', KA_SANS)}`,
    href: 'https://fonts.googleapis.com/css2?family=Pinyon+Script&family=Montserrat:wght@500;600;700&display=swap',
  },
]

/**
 * The Georgian part of a stack — everything after the leading Latin face.
 *
 * Every stack above is built as `'<latin>', ${ka(...)}`, so dropping the first
 * family leaves the Georgian companion and its Noto backstop. InvitationPage
 * sets the date, amount and label lines in this alone: they mix digits with
 * ქართული, and letting the Latin face keep the digits leaves them visibly
 * undersized beside the Georgian.
 */
export const kaTail = (stack: string) => {
  const comma = stack.indexOf(',')
  // A single-family stack has no Latin face to strip, and returning it whole
  // would set the Georgian-only lines in a face with no Georgian coverage.
  return comma < 0 ? KA_SANS : stack.slice(comma + 1).trim()
}

const FONT_IDS = INVITE_FONTS.map((f) => f.id)

const cleanFontId = (value?: string | null) =>
  value && FONT_IDS.includes(value) ? value : ''

export const getInviteFont = (id?: string | null) =>
  INVITE_FONTS.find((f) => f.id === id) || null

/**
 * Load a pairing's webfonts on demand. Repeat calls for the same href are
 * no-ops, so the picker can call this for every chip it shows.
 */
export const loadInviteFont = (id?: string | null) => {
  const href = getInviteFont(id)?.href
  if (!href || typeof document === 'undefined') return
  if (document.querySelector(`link[data-inv-font="${href}"]`)) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  link.dataset.invFont = href
  document.head.appendChild(link)
}

/* ------------------------------------------------------------- the bundle */

export type InviteLook = {
  accent?: string | null
  background?: string | null
  font?: string | null
  /**
   * The template's own accent, so a background override on its own can still
   * be checked for contrast against the colour the design actually uses.
   */
  templateAccent?: string | null
  /**
   * The template's own paper, so an accent override on its own is corrected
   * against the colour it will actually sit on (a dark template's paper is
   * not white).
   */
  templatePaper?: string | null
}

/**
 * The inline custom properties for a host's overrides, plus the classes the
 * invitation root needs so the flat background and dark-paper adjustments can
 * out-specify the template rules (both live at the end of InvitationPage.css).
 */
export const buildInviteLook = (look: InviteLook) => {
  const rawAccent = cleanHex(look.accent)
  const background = cleanHex(look.background)
  const font = getInviteFont(cleanFontId(look.font))

  const style: Record<string, string> = {}
  const classes: string[] = []

  // The paper the accent will actually sit on: the host's, if they picked one,
  // else the template's own.
  const paper = background || cleanHex(look.templatePaper) || ''
  // Correct the accent for the paper. Only the lightness moves — the hue the
  // host picked is what they asked for.
  const accent = rawAccent && paper ? legibleOn(rawAccent, paper) : rawAccent

  if (accent) {
    classes.push('inv-accent-custom')
    style['--inv-a1'] = accent
    style['--inv-a2'] = isDarkColor(paper || '#ffffff') ? tint(accent, 0.14) : shade(accent, 0.18)
    style['--inv-gold'] = tint(accent, 0.34)
    style['--inv-title'] = accent
    style['--inv-btn'] = accent
    style['--inv-btn-hover'] = shade(accent, 0.22)
    style['--inv-btn-off'] = tint(accent, 0.62)
    // Solid buttons hardcode white labels. Once the accent is light enough
    // that white disappears into it, the labels go dark instead.
    if (contrastRatio(accent, '#ffffff') < 2.6) classes.push('inv-btn-ink-dark')
  }

  if (background) {
    const dark = isDarkColor(background)
    classes.push('inv-bg-custom')
    if (dark) classes.push('inv-bg-dark')
    style['--inv-paper'] = background
    // The card sits just off the paper so it still reads as a separate sheet.
    style['--inv-card'] = dark ? tint(background, 0.1) : tint(background, 0.6)
    style['--inv-line'] = dark ? tint(background, 0.2) : shade(background, 0.14)
    // Type keeps the paper's hue but swings to whichever end stays legible.
    style['--inv-ink'] = dark ? tint(background, 0.88) : shade(background, 0.8)
    style['--inv-muted'] = dark ? tint(background, 0.55) : shade(background, 0.45)

    if (!accent) {
      // Only the paper was changed, so the template keeps its own accent —
      // which may now be invisible on it. Correct that one instead.
      const tplAccent = cleanHex(look.templateAccent)
      if (tplAccent) {
        const fixed = legibleOn(tplAccent, background)
        if (fixed !== tplAccent) {
          classes.push('inv-accent-custom')
          style['--inv-a1'] = fixed
          style['--inv-a2'] = dark ? tint(fixed, 0.14) : shade(fixed, 0.18)
          style['--inv-title'] = fixed
          style['--inv-btn'] = fixed
          style['--inv-btn-hover'] = shade(fixed, 0.22)
          if (contrastRatio(fixed, '#ffffff') < 2.6) classes.push('inv-btn-ink-dark')
        }
      }
      // A template's own title colour can still vanish on dark paper.
      if (dark && !style['--inv-title']) style['--inv-title'] = tint(background, 0.88)
      style['--inv-btn-off'] = dark ? tint(background, 0.28) : shade(background, 0.25)
    }
  }

  if (font) {
    style['--inv-display'] = font.display
    style['--inv-serif'] = font.display
    style['--inv-body'] = font.body
    style['--inv-ui'] = font.ui || font.body
    style['--inv-ka'] = kaTail(font.body)
  }

  return {
    style: style as CSSProperties,
    className: classes.join(' '),
    /** True when nothing was overridden — the template renders as designed. */
    isDefault: !accent && !background && !font,
  }
}
